import React from 'react';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { ClipboardText, Database, Gear, Globe, PencilSimple, Power } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import { IMcpStatus } from '../../../hooks/useMcp';
import { formatarBytes, formatarDataHora } from '../mcpUtils';
import {
  AZUL,
  Aviso,
  Cartao,
  Etiqueta,
  Eyebrow,
  IconeRedondo,
  Ladrilho,
  Linha,
  MONO,
  Recolhivel,
  TEXTO,
  TEXTO_SUAVE,
} from '../ui';

function Numero24({
  valor,
  legenda,
  cor = TEXTO,
}: {
  valor: number;
  legenda: string;
  cor?: string;
}) {
  return (
    <Box flex="1 1 90px" minW={0}>
      <Text fontFamily={MONO} fontSize="26px" fontWeight={600} color={cor} lineHeight="30px">
        {valor.toLocaleString('pt-BR')}
      </Text>
      <Text fontSize="12px" color={TEXTO_SUAVE}>
        {legenda}
      </Text>
    </Box>
  );
}

export function VisaoGeralTab({
  status,
  carregando,
  erro,
  detalheErro,
}: {
  status?: IMcpStatus;
  carregando: boolean;
  erro: boolean;
  detalheErro?: string;
}) {
  const navigate = useNavigate();

  if (carregando && !status) {
    return (
      <Flex justify="center" py={10}>
        <Spinner color={AZUL} />
      </Flex>
    );
  }
  if (erro || !status) {
    return (
      <Cartao p="18px 20px">
        <Aviso tom="perigo">{detalheErro ?? 'Não foi possível carregar o estado do MCP.'}</Aviso>
      </Cartao>
    );
  }

  const { config, auditoria, acesso_ip: acessoIp, redis_ok: redisOk } = status;

  const configuracao: [string, string][] = [
    ['Servidor', `${config.servidor.title ?? config.servidor.name} v${config.servidor.version}`],
    ['Protocolo', config.protocolos.join(', ')],
    [
      'Limite por minuto',
      `${config.limite_leitura_por_min} leitura · ${config.limite_escrita_por_min} escrita`,
    ],
    ['Confirmação', `${config.confirmacao_ttl_s} s, uso único`],
    ['Corpo máximo', formatarBytes(config.limite_corpo_bytes)],
    ['Itens por resposta', `até ${config.limite_itens_resposta}`],
    [
      'Origens de navegador',
      config.origens_permitidas.length > 0 ? config.origens_permitidas.join(', ') : 'nenhuma',
    ],
  ];

  return (
    <Flex direction="column" gap="16px">
      {!config.ativo && (
        <Aviso tom="aviso">
          MCP desligado neste ambiente: o endereço responde 404. Ligue com MCP_ATIVO=true na API.
        </Aviso>
      )}

      <Flex gap="12px" wrap="wrap">
        <Ladrilho
          icone={<Power size={18} weight="bold" />}
          rotulo="Servidor"
          valor={config.ativo ? 'Ligado' : 'Desligado'}
          tom={config.ativo ? 'sucesso' : 'perigo'}
          dica="MCP_ATIVO"
        />
        <Ladrilho
          icone={<PencilSimple size={18} weight="bold" />}
          rotulo="Escrita da IA"
          valor={config.escrita_ativa ? 'Ligada' : 'Só leitura'}
          tom={config.escrita_ativa ? 'aviso' : 'sucesso'}
          dica="MCP_ESCRITA_ATIVA"
        />
        <Ladrilho
          icone={<Database size={18} weight="bold" />}
          rotulo="Redis"
          valor={redisOk ? 'OK' : 'Fora do ar'}
          tom={redisOk ? 'sucesso' : 'perigo'}
          dica="Limite de uso e confirmações. Sem Redis, a escrita é recusada."
        />
        <Ladrilho
          icone={<ClipboardText size={18} weight="bold" />}
          rotulo="Auditoria"
          valor={auditoria.disponivel ? 'Gravando' : 'Sem tabela'}
          tom={auditoria.disponivel ? 'sucesso' : 'perigo'}
          dica="A migration do MCP precisa ter rodado."
        />
      </Flex>

      <Flex gap="12px" wrap="wrap" align="stretch">
        <Cartao
          p="16px 18px"
          flex="2 1 340px"
          minW={0}
          display="flex"
          flexDirection="column"
          gap="12px"
        >
          <Eyebrow>ÚLTIMAS 24 HORAS</Eyebrow>
          {auditoria.disponivel ? (
            <>
              <Flex gap="12px" wrap="wrap">
                <Numero24 valor={auditoria.total_24h} legenda="chamadas" />
                <Numero24 valor={auditoria.ok_24h} legenda="OK" cor="#166534" />
                <Numero24 valor={auditoria.negado_24h} legenda="negadas" cor="#92400E" />
                <Numero24 valor={auditoria.erro_24h} legenda="com erro" cor="#991B1B" />
              </Flex>
              {auditoria.por_ferramenta_24h.length > 0 && (
                <Flex gap="6px" wrap="wrap">
                  {auditoria.por_ferramenta_24h.slice(0, 5).map(f => (
                    <Etiqueta key={f.ferramenta} tom="info">
                      {f.ferramenta} · {f.total}
                    </Etiqueta>
                  ))}
                </Flex>
              )}
              <Text fontSize="12px" color={TEXTO_SUAVE}>
                Última chamada: {formatarDataHora(auditoria.ultima_chamada)}
              </Text>
            </>
          ) : (
            <Text fontSize="13px" color={TEXTO_SUAVE}>
              Sem dados: falta a tabela de auditoria.
            </Text>
          )}
        </Cartao>

        <Cartao
          p="16px 18px"
          flex="1 1 260px"
          minW={0}
          display="flex"
          flexDirection="column"
          gap="10px"
        >
          <Eyebrow>ACESSO POR IP</Eyebrow>
          <Flex align="center" gap="10px">
            <IconeRedondo icone={<Globe size={18} weight="bold" />} tom="sucesso" />
            <Box minW={0}>
              <Text fontFamily={MONO} fontSize="15px" fontWeight={600} color={TEXTO}>
                {acessoIp.ip_atual ?? 'desconhecido'}
              </Text>
              <Text fontSize="12px" color={TEXTO_SUAVE}>
                seu IP · {acessoIp.entradas_ativas} liberado(s)
              </Text>
            </Box>
          </Flex>
          <Button
            size="xs"
            variant="link"
            color={AZUL}
            alignSelf="flex-start"
            onClick={() => navigate('/configuracoes/ip-whitelist')}
          >
            Gerenciar IPs liberados
          </Button>
        </Cartao>
      </Flex>

      <Recolhivel
        icone={<Gear size={16} />}
        titulo="Configuração em vigor"
        resumo="Limites, protocolo e origens aceitas"
      >
        <Box mx="-18px">
          {configuracao.map(([rotulo, valor]) => (
            <Linha key={rotulo} justify="space-between" p="8px 18px">
              <Text fontSize="13px" fontWeight={600} color="#334155" flex="1 1 160px">
                {rotulo}
              </Text>
              <Text fontSize="13px" color={TEXTO} flex="2 1 220px" textAlign={{ md: 'right' }}>
                {valor}
              </Text>
            </Linha>
          ))}
        </Box>
      </Recolhivel>
    </Flex>
  );
}
